import React from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { ProviderAggregate } from "../../../../types/analytics";
import { CHART_COLORS, CHART_FONT, PROVIDER_COLORS, formatNumber } from "./chartConfig";

interface ProviderStatsPanelProps {
  providers: ProviderAggregate[] | null;
  loading: boolean;
}

const ProviderStatsPanel: React.FC<ProviderStatsPanelProps> = ({
  providers,
  loading,
}) => {
  if (loading) {
    return (
      <Card className="shadow-2 h-full" title="Provider Stats">
        <div className="flex flex-column gap-3">
          <Skeleton width="100%" height="200px" />
          <Skeleton width="100%" height="100px" />
        </div>
      </Card>
    );
  }

  if (!providers || providers.length === 0) {
    return (
      <Card className="shadow-2 h-full" title="Provider Stats">
        <div className="flex flex-column align-items-center justify-content-center text-center p-5">
          <i className="pi pi-sitemap text-4xl text-300 mb-3" />
          <p className="text-500">No provider data available</p>
        </div>
      </Card>
    );
  }

  const totalSessions = providers.reduce((s, p) => s + p.sessions, 0);

  const chartData = {
    labels: providers.map((p) => p.provider),
    datasets: [
      {
        data: providers.map((p) => p.sessions),
        backgroundColor: providers.map(
          (_, i) => PROVIDER_COLORS[i % PROVIDER_COLORS.length]
        ),
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: CHART_COLORS.primary,
        titleFont: { ...CHART_FONT, weight: "bold" as const },
        bodyFont: CHART_FONT,
        cornerRadius: 8,
        callbacks: {
          label: (ctx: any) => {
            const pct =
              totalSessions > 0
                ? ((ctx.parsed / totalSessions) * 100).toFixed(1)
                : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  };

  const tableData = providers.map((p) => ({
    ...p,
    sharePercent:
      totalSessions > 0
        ? ((p.sessions / totalSessions) * 100).toFixed(0)
        : "0",
  }));

  return (
    <Card className="shadow-2 h-full" title="Provider Stats">
      <div className="flex flex-column gap-3">
        <div className="flex justify-content-center" style={{ maxWidth: "250px", margin: "0 auto" }}>
          <Chart type="doughnut" data={chartData} options={chartOptions} />
        </div>
        <div>
          <DataTable value={tableData} size="small" stripedRows>
            <Column
              field="provider"
              header="Provider"
              style={{ minWidth: "10rem" }}
              body={(row, { rowIndex }) => (
                <div className="flex align-items-center gap-2 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      minWidth: 10,
                      borderRadius: "50%",
                      backgroundColor:
                        PROVIDER_COLORS[rowIndex % PROVIDER_COLORS.length],
                    }}
                  />
                  <span className="overflow-hidden text-overflow-ellipsis">{row.provider}</span>
                </div>
              )}
            />
            <Column field="sessions" header="Sessions" align="right" />
            <Column
              field="sharePercent"
              header="Share"
              body={(row) => `${row.sharePercent}%`}
              align="right"
            />
            <Column
              field="energy"
              header="kWh"
              body={(row) => formatNumber(row.energy)}
              align="right"
            />
            <Column
              field="revenue"
              header="Revenue"
              body={(row) => `${formatNumber(row.revenue)} \u20AC`}
              align="right"
            />
            <Column field="uniqueUsers" header="Users" align="right" />
          </DataTable>
        </div>
      </div>
    </Card>
  );
};

export default ProviderStatsPanel;
